import { useState } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NAV_LINKS, TREATMENT_LINKS } from '@/data/navigation';
import { useTrackEvent } from '@/hooks/useTrackEvent';
import { useWhatsAppLinkDaPagina } from '@/hooks/useWhatsAppLinkDaPagina';
import logoHabitus from '@/assets/logo-habitus.png?format=webp';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTratamentosOpen, setIsTratamentosOpen] = useState(false);
  const { trackEventBeforeNavigation } = useTrackEvent();
  // Cada página manda a sua mensagem — é por ela que os relatórios sabem de
  // onde veio o contato.
  const whatsappLink = useWhatsAppLinkDaPagina();

  const handleWhatsAppClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    trackEventBeforeNavigation('whatsapp_click', { source: 'header' }, whatsappLink);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container-mobile">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <img 
              src={logoHabitus} 
              alt="Habitus Saúde Integrativa" 
              width={646}
              height={162}
              className="h-10 md:h-12 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.slice(0, 2).map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                {link.label}
              </a>
            ))}
            
            {/* Tratamentos Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setIsTratamentosOpen(true)}
              onMouseLeave={() => setIsTratamentosOpen(false)}
            >
              <button className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary transition-colors py-2">
                Tratamentos
                <ChevronDown className={`w-4 h-4 transition-transform ${isTratamentosOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Renderizado SEMPRE, escondido por CSS -- nao com
                  `{isTratamentosOpen && ...}`. Com a renderizacao condicional os
                  11 links de tratamento so existiam no DOM depois do clique, e
                  crawler nao clica nem passa o mouse: nem o do Google, nem o de
                  LLM. Na pratica /ozonioterapia, /bioimpedancia e outras 9
                  paginas nao recebiam link interno de lugar nenhum (ver
                  docs/SEO-GEO.md). Escondendo por CSS os links ficam no HTML e
                  o comportamento visual continua igual. */}
              <div
                className={`absolute top-full left-0 pt-1 ${isTratamentosOpen ? '' : 'hidden'}`}
              >
                <div className="w-56 bg-card border border-border rounded-xl shadow-lg z-50 py-2 animate-fade-in">
                  {TREATMENT_LINKS.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted hover:text-primary transition-colors"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
            
            {NAV_LINKS.slice(2).map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a href="tel:+5561996120137" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
              <Phone className="w-4 h-4" />
              <span className="hidden lg:inline">(61) 99612-0137</span>
            </a>
            <Button asChild className="bg-primary hover:bg-primary/90">
              <a 
                href={whatsappLink} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={handleWhatsAppClick}
              >
                Agendar
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-3 -mr-1 text-foreground"
            aria-label="Menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col gap-2">
              {NAV_LINKS.slice(0, 2).map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="py-3 px-2 text-base font-medium text-foreground hover:text-primary hover:bg-muted/50 rounded-md transition-colors"
                >
                  {link.label}
                </a>
              ))}
              
              {/* Tratamentos Mobile */}
              <div className="py-2">
                <button
                  onClick={() => setIsTratamentosOpen(!isTratamentosOpen)}
                  className="w-full flex items-center justify-between py-3 px-2 text-base font-medium text-foreground hover:text-primary hover:bg-muted/50 rounded-md transition-colors"
                >
                  Tratamentos
                  <ChevronDown className={`w-4 h-4 transition-transform ${isTratamentosOpen ? 'rotate-180' : ''}`} />
                </button>
                {isTratamentosOpen && (
                  <div className="pl-4 mt-1 space-y-1">
                    {TREATMENT_LINKS.map((item) => (
                      <a
                        key={item.href}
                        href={item.href}
                        onClick={() => setIsMenuOpen(false)}
                        className="block py-2 px-2 text-sm text-muted-foreground hover:text-primary hover:bg-muted/50 rounded-md transition-colors"
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              
              {NAV_LINKS.slice(2).map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="py-3 px-2 text-base font-medium text-foreground hover:text-primary hover:bg-muted/50 rounded-md transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-4 mt-2 border-t border-border">
                <Button asChild className="w-full bg-primary hover:bg-primary/90">
                  <a 
                    href={whatsappLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={handleWhatsAppClick}
                  >
                    Agendar pelo WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
